import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  UserCheck,
  UserX,
  ShieldCheck,
  UsersRound,
  PencilRuler,
  UserPlus,
  MoreVertical,
  Shield,
  KeyRound,
  Trash2,
  Power,
  PowerOff,
  Pencil,
  FilterX,
  Download,
  Lock,
  ArrowUpDown,
  Copy,
  ExternalLink,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/hooks/use-auth";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { StatCard } from "@/components/stat-card";
import { TableSkeleton } from "@/components/loading";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  STAFF_ROLES,
  activateStaff,
  addStaffRole,
  bulkSetStaffActive,
  createStaff,
  deactivateStaff,
  deleteStaff,
  exportStaffCsv,
  fetchStaffDetail,
  fetchStaffGrid,
  fetchStaffOverview,
  removeStaffRole,
  resetStaffPassword,
  updateStaff,
  type StaffFilterQuery,
  type StaffGridRow,
  type StaffRole,
} from "@/lib/api/staff";
import { ApiError } from "@/lib/api/client";

export const Route = createFileRoute("/_authenticated/admin/staff")({
  component: StaffRoute,
});

const PAGE_SIZE = 20;

function StaffRoute() {
  return (
    <RequirePermission
      action="manage:users"
      fallback={<AccessDenied message="You don't have permission to manage staff." />}
    >
      <StaffManagementPage />
    </RequirePermission>
  );
}

/* ── helpers ─────────────────────────────────────────────────────────────── */

function roleLabel(role: string): string {
  switch (role) {
    case "SuperAdmin":
      return "Super Admin";
    case "SystemAdmin":
      return "System Admin";
    case "TeamLeader":
      return "Team Leader";
    case "ContentCreator":
      return "Content Creator";
    default:
      return role;
  }
}

function primaryRole(roles: string[]): string {
  if (roles.includes("SuperAdmin")) return "SuperAdmin";
  if (roles.includes("SystemAdmin")) return "SystemAdmin";
  if (roles.includes("TeamLeader")) return "TeamLeader";
  if (roles.includes("ContentCreator")) return "ContentCreator";
  return roles[0] ?? "";
}

function managedLabel(row: StaffGridRow): string {
  const role = primaryRole(row.roles);
  if (role === "TeamLeader") return `${row.managedCount} groups`;
  if (role === "ContentCreator") return `${row.managedCount} courses`;
  return "—";
}

function formatDate(value: string | null): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name: string | null, email: string): string {
  const base = (name || email || "?").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === "SuperAdmin"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
      : role === "SystemAdmin"
        ? "bg-primary/10 text-primary border-primary/20"
        : role === "TeamLeader"
          ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
          : "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20";
  return (
    <Badge variant="outline" className={cls}>
      {role === "SuperAdmin" && <Crown className="h-3 w-3 mr-1" />}
      {roleLabel(role)}
    </Badge>
  );
}

function StatusBadge({ row }: { row: StaffGridRow }) {
  if (row.isLocked) {
    return (
      <Badge
        variant="outline"
        className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
      >
        <Lock className="h-3 w-3 mr-1" />
        Locked
      </Badge>
    );
  }
  if (row.isActive) {
    return (
      <Badge
        variant="outline"
        className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
      >
        Active
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-muted text-muted-foreground">
      Inactive
    </Badge>
  );
}

/* ── main page ───────────────────────────────────────────────────────────── */

function StaffManagementPage() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();

  // filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<Set<StaffRole>>(new Set());
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [sortBy, setSortBy] = useState("createdat");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  // selection + panels
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(
    null,
  );

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const rolesArray = useMemo(() => Array.from(roleFilter), [roleFilter]);
  const rolesKey = rolesArray.join(",");

  // reset page + selection when filters change
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [search, activeFilter, sortBy, sortDir, rolesKey]);

  const filterQuery: StaffFilterQuery = useMemo(
    () => ({
      pageNumber: page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      roles: rolesArray.length > 0 ? rolesArray : undefined,
      isActive: activeFilter === "all" ? undefined : activeFilter === "active",
      sortBy,
      sortDir,
    }),
    [page, search, rolesArray, activeFilter, sortBy, sortDir],
  );

  const { data: overview } = useQuery({
    queryKey: ["staff-overview"],
    queryFn: fetchStaffOverview,
  });

  const { data: gridData, isLoading } = useQuery({
    queryKey: ["staff-grid", filterQuery],
    queryFn: () => fetchStaffGrid(filterQuery),
  });

  const rows = gridData?.items ?? [];

  const hasActiveFilters = !!search || roleFilter.size > 0 || activeFilter !== "all";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["staff-grid"] });
    qc.invalidateQueries({ queryKey: ["staff-overview"] });
  };

  const onlyOneAdmin = (overview?.systemAdmins ?? 0) <= 1;
  const isProtectedAdmin = (row: StaffGridRow) => onlyOneAdmin && row.roles.includes("SystemAdmin");

  const toggleRoleFilter = (role: StaffRole) => {
    const next = new Set(roleFilter);
    if (next.has(role)) next.delete(role);
    else next.add(role);
    setRoleFilter(next);
  };

  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      rows.forEach((r) => next.delete(r.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      rows.forEach((r) => next.add(r.id));
      setSelectedIds(next);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setRoleFilter(new Set());
    setActiveFilter("all");
    setSortBy("createdat");
    setSortDir("desc");
  };

  // bulk activate/deactivate
  const bulkMutation = useMutation({
    mutationFn: (action: "activate" | "deactivate") =>
      bulkSetStaffActive(Array.from(selectedIds), action),
    onSuccess: (result) => {
      if (result.failureCount === 0) {
        toast.success(`${result.successCount} account(s) updated.`);
      } else {
        const firstError = result.results.find((r) => !r.success)?.error;
        toast.warning(
          `${result.successCount} succeeded, ${result.failureCount} failed.${firstError ? ` ${firstError}` : ""}`,
        );
      }
      setSelectedIds(new Set());
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Bulk action failed")),
  });

  const exportMutation = useMutation({
    mutationFn: () => exportStaffCsv(filterQuery),
    onError: (e) => toast.error(errorMessage(e, "Export failed")),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <PencilRuler className="h-7 w-7 text-primary" />
            Staff Management
          </h1>
          <p className="text-muted-foreground">
            Create, promote, and manage staff and admin accounts, roles, and access.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Overview */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <StatCard
          icon={<Users className="h-5 w-5" />}
          label="Total Staff"
          value={overview?.totalStaff ?? "—"}
          color="primary"
        />
        <StatCard
          icon={<UserCheck className="h-5 w-5" />}
          label="Active"
          value={overview?.activeStaff ?? "—"}
          color="green"
        />
        <StatCard
          icon={<UserX className="h-5 w-5" />}
          label="Inactive"
          value={overview?.inactiveStaff ?? "—"}
          sub={overview?.lockedStaff ? `${overview.lockedStaff} locked` : undefined}
          color="muted"
        />
        <StatCard
          icon={<UsersRound className="h-5 w-5" />}
          label="Team Leaders"
          value={overview?.teamLeaders ?? "—"}
          color="secondary"
        />
        <StatCard
          icon={<PencilRuler className="h-5 w-5" />}
          label="Content Creators"
          value={overview?.contentCreators ?? "—"}
          color="amber"
        />
        <StatCard
          icon={<ShieldCheck className="h-5 w-5" />}
          label="System Admins"
          value={overview?.systemAdmins ?? "—"}
          sub={
            overview?.superAdmins
              ? `${overview.superAdmins} Super Admin${overview.superAdmins === 1 ? "" : "s"}`
              : undefined
          }
          color="red"
        />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-xs"
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Shield className="h-4 w-4" />
              Role
              {roleFilter.size > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {roleFilter.size}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Filter by role</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {STAFF_ROLES.map((role) => (
              <DropdownMenuCheckboxItem
                key={role}
                checked={roleFilter.has(role)}
                onCheckedChange={() => toggleRoleFilter(role)}
                onSelect={(e) => e.preventDefault()}
              >
                {roleLabel(role)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Select
          value={activeFilter}
          onValueChange={(v) => setActiveFilter(v as typeof activeFilter)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="createdat">Created date</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="role">Role</SelectItem>
            <SelectItem value="lastlogin">Last login</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="icon"
          title={sortDir === "asc" ? "Ascending" : "Descending"}
          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
        >
          <ArrowUpDown className="h-4 w-4" />
        </Button>

        <div className="flex-1" />

        <Button
          variant="outline"
          className="gap-2"
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending}
        >
          <Download className="h-4 w-4" />
          Export
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" className="gap-2" onClick={clearFilters}>
            <FilterX className="h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-4 py-2">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={bulkMutation.isPending}
            onClick={() => bulkMutation.mutate("activate")}
          >
            <Power className="h-4 w-4" />
            Activate
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={bulkMutation.isPending}
            onClick={() => bulkMutation.mutate("deactivate")}
          >
            <PowerOff className="h-4 w-4" />
            Deactivate
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Managed</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="p-4">
                  <TableSkeleton rows={6} columns={8} className="border-none" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-16 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                      <Users className="h-7 w-7 opacity-40" />
                    </div>
                    <div className="space-y-0.5">
                      <p className="font-medium text-foreground">No staff found</p>
                      <p className="text-xs text-muted-foreground">
                        {hasActiveFilters
                          ? "Try adjusting or clearing your filters."
                          : "Add a staff member to get started."}
                      </p>
                    </div>
                    {hasActiveFilters && (
                      <Button variant="outline" size="sm" className="gap-2" onClick={clearFilters}>
                        <FilterX className="h-4 w-4" />
                        Clear filters
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <StaffRowItem
                  key={row.id}
                  row={row}
                  selected={selectedIds.has(row.id)}
                  onToggleSelect={() => toggleSelection(row.id)}
                  onOpenDetail={() => setDetailId(row.id)}
                  isProtectedAdmin={isProtectedAdmin(row)}
                  canManageSuperAdmin={isSuperAdmin}
                  onTempPassword={setTempPassword}
                  invalidate={invalidate}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {gridData && gridData.totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (gridData.hasPreviousPage) setPage((p) => Math.max(1, p - 1));
                }}
                className={!gridData.hasPreviousPage ? "pointer-events-none opacity-40" : undefined}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#" isActive className="pointer-events-none">
                {gridData.pageNumber} / {gridData.totalPages}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (gridData.hasNextPage) setPage((p) => p + 1);
                }}
                className={!gridData.hasNextPage ? "pointer-events-none opacity-40" : undefined}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* Result summary */}
      {!isLoading && gridData && (
        <p className="text-xs text-muted-foreground">
          {gridData.totalCount} staff member{gridData.totalCount === 1 ? "" : "s"}
        </p>
      )}

      {/* Create dialog */}
      <CreateStaffDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onTempPassword={setTempPassword}
        invalidate={invalidate}
        canGrantSuperAdmin={isSuperAdmin}
      />

      {/* Detail / edit sheet */}
      <StaffDetailSheet
        staffId={detailId}
        onClose={() => setDetailId(null)}
        invalidate={invalidate}
      />

      {/* Temp password dialog */}
      <TempPasswordDialog value={tempPassword} onClose={() => setTempPassword(null)} />
    </div>
  );
}

/* ── row ─────────────────────────────────────────────────────────────────── */

function StaffRowItem({
  row,
  selected,
  onToggleSelect,
  onOpenDetail,
  isProtectedAdmin,
  canManageSuperAdmin,
  onTempPassword,
  invalidate,
}: {
  row: StaffGridRow;
  selected: boolean;
  onToggleSelect: () => void;
  onOpenDetail: () => void;
  isProtectedAdmin: boolean;
  /** Only a Super Administrator may grant or revoke the Super Administrator role — the backend
   *  independently enforces this; hiding the option here just avoids a round-trip 403. */
  canManageSuperAdmin: boolean;
  onTempPassword: (v: { email: string; password: string }) => void;
  invalidate: () => void;
}) {
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const rolesNotHeld = STAFF_ROLES.filter(
    (r) => !row.roles.includes(r) && (r !== "SuperAdmin" || canManageSuperAdmin),
  );
  const removableRoles = row.roles.filter((r) => r !== "SuperAdmin" || canManageSuperAdmin);

  const addRoleMutation = useMutation({
    mutationFn: (role: string) => addStaffRole(row.id, role),
    onSuccess: (_data, role) => {
      toast.success(`${roleLabel(role)} role added`);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to add role")),
  });

  const removeRoleMutation = useMutation({
    mutationFn: (role: string) => removeStaffRole(row.id, role),
    onSuccess: (_data, role) => {
      toast.success(`${roleLabel(role)} role removed`);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to remove role")),
  });

  const activeMutation = useMutation({
    mutationFn: () => (row.isActive ? deactivateStaff(row.id) : activateStaff(row.id)),
    onSuccess: () => {
      toast.success(row.isActive ? "Account deactivated" : "Account activated");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to update account")),
  });

  const resetMutation = useMutation({
    mutationFn: () => resetStaffPassword(row.id),
    onSuccess: (res) => {
      setConfirmReset(false);
      onTempPassword({ email: row.email, password: res.tempPassword });
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to reset password")),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteStaff(row.id),
    onSuccess: () => {
      toast.success("Account deleted");
      setConfirmDelete(false);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to delete account")),
  });

  return (
    <TableRow className="cursor-pointer" onClick={onOpenDetail}>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={onToggleSelect} aria-label="Select row" />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {row.avatarUrl ? (
              <AvatarImage src={row.avatarUrl} alt={row.fullName ?? row.email} />
            ) : null}
            <AvatarFallback className="text-xs font-semibold">
              {initials(row.fullName, row.email)}
            </AvatarFallback>
          </Avatar>
          <span className="font-medium truncate max-w-[180px]">{row.fullName || "—"}</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground truncate max-w-[220px]">{row.email}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {row.roles.map((r) => (
            <RoleBadge key={r} role={r} />
          ))}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge row={row} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(row.lastLoginDate)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(row.createdAt)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{managedLabel(row)}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpenDetail}>
              <Pencil className="h-4 w-4 mr-2" /> Edit profile
            </DropdownMenuItem>
            {rolesNotHeld.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Shield className="h-4 w-4 mr-2" /> Add role
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {rolesNotHeld.map((r) => (
                      <DropdownMenuItem key={r} onClick={() => addRoleMutation.mutate(r)}>
                        {roleLabel(r)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
            {row.roles.length > 1 && removableRoles.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Shield className="h-4 w-4 mr-2" /> Remove role
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {removableRoles.map((r) => (
                      <DropdownMenuItem
                        key={r}
                        onClick={() => removeRoleMutation.mutate(r)}
                        disabled={
                          (r === "SystemAdmin" && isProtectedAdmin) ||
                          (r === "SystemAdmin" && row.roles.includes("SuperAdmin"))
                        }
                        className="text-destructive"
                      >
                        {roleLabel(r)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
            <DropdownMenuItem
              onClick={() => activeMutation.mutate()}
              disabled={activeMutation.isPending || (row.isActive && isProtectedAdmin)}
            >
              {row.isActive ? (
                <>
                  <PowerOff className="h-4 w-4 mr-2" /> Deactivate
                </>
              ) : (
                <>
                  <Power className="h-4 w-4 mr-2" /> Activate
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setConfirmReset(true)}>
              <KeyRound className="h-4 w-4 mr-2" /> Reset password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setConfirmDelete(true)}
              disabled={isProtectedAdmin}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Reset password confirm */}
        <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset password?</AlertDialogTitle>
              <AlertDialogDescription>
                A new temporary password will be generated for {row.email}. They will be required to
                change it on next login.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  resetMutation.mutate();
                }}
              >
                {resetMutation.isPending ? "Resetting…" : "Reset password"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete confirm */}
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete account?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes {row.email} from the platform. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  deleteMutation.mutate();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete account"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}

/* ── create dialog ───────────────────────────────────────────────────────── */

function CreateStaffDialog({
  open,
  onOpenChange,
  onTempPassword,
  invalidate,
  canGrantSuperAdmin,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onTempPassword: (v: { email: string; password: string }) => void;
  invalidate: () => void;
  canGrantSuperAdmin: boolean;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<string>("TeamLeader");
  const creatableRoles = STAFF_ROLES.filter((r) => r !== "SuperAdmin" || canGrantSuperAdmin);
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setFullName("");
      setRole("TeamLeader");
      setPassword("");
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () =>
      createStaff({
        email: email.trim().toLowerCase(),
        fullName: fullName.trim() || undefined,
        role,
        password: password.trim() || undefined,
      }),
    onSuccess: (res) => {
      if (res.tempPassword) {
        onTempPassword({ email: email.trim().toLowerCase(), password: res.tempPassword });
      } else {
        toast.success("Staff account created");
      }
      onOpenChange(false);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to create staff account")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add staff member</DialogTitle>
          <DialogDescription>
            Create a new staff account. Leave the password blank to auto-generate a temporary one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Full name</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {creatableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {roleLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>
              Password <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Auto-generated if blank"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!email || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating…" : "Create staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── detail sheet ────────────────────────────────────────────────────────── */

function StaffDetailSheet({
  staffId,
  onClose,
  invalidate,
}: {
  staffId: string | null;
  onClose: () => void;
  invalidate: () => void;
}) {
  const { data: detail, isLoading } = useQuery({
    queryKey: ["staff-detail", staffId],
    queryFn: () => fetchStaffDetail(staffId!),
    enabled: !!staffId,
  });

  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    if (detail) {
      setFullName(detail.fullName ?? "");
      setPhoneNumber(detail.phoneNumber ?? "");
    }
  }, [detail]);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateStaff(staffId!, {
        fullName: fullName.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("Profile updated");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e, "Failed to update profile")),
  });

  return (
    <Sheet open={!!staffId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-card border-l">
        <SheetHeader className="border-b pb-4 mb-4">
          <SheetTitle>Staff details</SheetTitle>
        </SheetHeader>

        {isLoading || !detail ? (
          <div className="space-y-4">
            <TableSkeleton rows={4} columns={1} className="border-none" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Identity */}
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                {detail.avatarUrl ? (
                  <AvatarImage src={detail.avatarUrl} alt={detail.fullName ?? detail.email} />
                ) : null}
                <AvatarFallback className="text-sm font-semibold">
                  {initials(detail.fullName, detail.email)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="font-semibold truncate">{detail.fullName || "—"}</p>
                <p className="text-sm text-muted-foreground truncate">{detail.email}</p>
              </div>
              <div className="ml-auto flex flex-col items-end gap-1">
                <div className="flex flex-wrap justify-end gap-1">
                  {detail.roles.map((r) => (
                    <RoleBadge key={r} role={r} />
                  ))}
                </div>
                <StatusBadge row={detail} />
              </div>
            </div>

            {/* Profile edit */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Profile</h3>
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Phone number</Label>
                <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
              </div>
              <Button
                size="sm"
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? "Saving…" : "Save profile"}
              </Button>
            </div>

            {/* Security */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Security</h3>
              <div className="rounded-lg border divide-y text-sm">
                <div className="flex justify-between px-3 py-2">
                  <span className="text-muted-foreground">Locked</span>
                  <span>
                    {detail.isLocked ? `Yes (until ${formatDateTime(detail.lockoutEnd)})` : "No"}
                  </span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-muted-foreground">Failed login attempts</span>
                  <span>{detail.accessFailedCount}</span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-muted-foreground">Email confirmed</span>
                  <span>{detail.emailConfirmed ? "Yes" : "No"}</span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-muted-foreground">Must change password</span>
                  <span>
                    {detail.mustChangePassword ? (
                      <Badge
                        variant="outline"
                        className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                      >
                        Required
                      </Badge>
                    ) : (
                      "No"
                    )}
                  </span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-muted-foreground">Last login</span>
                  <span>{formatDateTime(detail.lastLoginDate)}</span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-muted-foreground">Created</span>
                  <span>{formatDateTime(detail.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Recent activity */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Recent activity</h3>
                <a
                  href={`/admin/audit-logs?entityType=User&entityId=${detail.id}`}
                  className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
                >
                  Full history <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              {detail.recentActivity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent activity.</p>
              ) : (
                <ul className="space-y-1">
                  {detail.recentActivity.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {a.actionType}
                          {a.entityName ? ` · ${a.entityName}` : ""}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(a.timestamp)}
                        </p>
                      </div>
                      <span
                        className={`h-2 w-2 rounded-full shrink-0 ${a.isSuccess ? "bg-green-500" : "bg-red-500"}`}
                        title={a.isSuccess ? "Success" : "Failed"}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ── temp password dialog ────────────────────────────────────────────────── */

function TempPasswordDialog({
  value,
  onClose,
}: {
  value: { email: string; password: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Temporary password</DialogTitle>
          <DialogDescription>
            Share this password with {value?.email}. It won&apos;t be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3">
          <code className="flex-1 break-all text-sm">{value?.password}</code>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (value?.password) {
                navigator.clipboard?.writeText(value.password);
                toast.success("Copied to clipboard");
              }
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
